import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import api, { getApiErrorMessage } from '../../services/api';

const COMMANDS = [
  {
    id: 'audit_order_snapshot_mismatches',
    labelKey: 'adminConsole.cmdAuditSnapshot',
    onlySummary: true,
  },
];

function AdminConsolePage() {
  const { t } = useLanguage();
  const [storeId, setStoreId] = useState('');
  const [running, setRunning] = useState(null);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [elapsedMs, setElapsedMs] = useState(null);
  const outputRef = useRef(null);

  const appendOutput = useCallback((text) => {
    setOutput((prev) => (prev ? `${prev}\n\n${text}` : text));
    requestAnimationFrame(() => {
      const el = outputRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const runCommand = async (cmd) => {
    setRunning(cmd.id);
    setError('');
    setElapsedMs(null);
    const started = new Date().toISOString();
    appendOutput(`> ${cmd.id}${storeId.trim() ? ` --store-id=${storeId.trim()}` : ''}${cmd.onlySummary ? ' --only-summary' : ''}\n[${started}]`);

    try {
      const body = await api.adminConsoleRun({
        command: cmd.id,
        store_id: storeId.trim() || undefined,
        only_summary: cmd.onlySummary !== false,
      });
      if (body?.error) {
        throw new Error(body.errorText || t('adminConsole.runFailed'));
      }
      appendOutput(body.output || '');
      if (body.elapsed_ms != null) {
        setElapsedMs(body.elapsed_ms);
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, t('adminConsole.runFailed'));
      setError(msg);
      appendOutput(`ERROR: ${msg}`);
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="admin-console-page">
      <h4 className="mb-3 fw-bold">{t('adminConsole.title')}</h4>
      <p className="text-muted small mb-3">{t('adminConsole.subtitle')}</p>

      <Card className="border-0 shadow-sm mb-3 admin-console-card">
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">{t('adminConsole.storeIdOptional')}</Form.Label>
            <Form.Control
              type="text"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder={t('adminConsole.storeIdPlaceholder')}
              disabled={Boolean(running)}
            />
          </Form.Group>

          <div className="d-flex flex-wrap gap-2">
            {COMMANDS.map((cmd) => (
              <Button
                key={cmd.id}
                variant="outline-warning"
                size="sm"
                disabled={Boolean(running)}
                onClick={() => runCommand(cmd)}
              >
                {running === cmd.id ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('adminConsole.running')}
                  </>
                ) : (
                  t(cmd.labelKey)
                )}
              </Button>
            ))}
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={Boolean(running) || !output}
              onClick={() => {
                setOutput('');
                setError('');
                setElapsedMs(null);
              }}
            >
              {t('adminConsole.clear')}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="py-2 small">
          {error}
        </Alert>
      )}

      {elapsedMs != null && !running && (
        <div className="small text-muted mb-2">
          {t('adminConsole.elapsed', { ms: elapsedMs })}
        </div>
      )}

      <Card className="border-0 shadow-sm admin-console-card">
        <Card.Header className="bg-transparent border-bottom py-2">
          <span className="small fw-semibold">{t('adminConsole.outputTitle')}</span>
        </Card.Header>
        <Card.Body className="p-0">
          <pre ref={outputRef} className="admin-console-output mb-0">
            {output || t('adminConsole.outputEmpty')}
          </pre>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AdminConsolePage;
